import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

/**
 * Capa de avisos (toasts) con tono. El Toast del sistema de diseño no expone
 * intención/estado (solo título + descripción), y el paquete es una dependencia
 * del registro que no se puede tocar desde aquí. Así que el aviso vive en la app,
 * pero pinta con los MISMOS tokens semánticos del sistema —`--fc-color-success`,
 * `--fc-color-warning`, `--fc-color-danger`, `--fc-color-action`— que ya usa el
 * Badge, para no inventar una paleta de severidad paralela.
 */

export type ToastTono = "success" | "error" | "warning" | "info";

interface Mensaje {
  id: number;
  tono: ToastTono;
  title: string;
  description?: string;
}

export interface ToastApi {
  /** Aviso genérico; por defecto tono "info". */
  toast: (title: string, opts?: { tono?: ToastTono; description?: string }) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast debe usarse dentro de <ToastProvider>");
  return ctx;
}

// Color, símbolo y urgencia de anuncio por tono. El color sale de un token
// semántico del sistema; el fallback cubre el caso de que `--fc-color-action`
// no esté definido para que el borde nunca quede inválido (sin color).
const TONO: Record<
  ToastTono,
  { color: string; simbolo: string; live: "polite" | "assertive" }
> = {
  success: { color: "var(--fc-color-success)", simbolo: "✓", live: "polite" },
  error: { color: "var(--fc-color-danger)", simbolo: "✕", live: "assertive" },
  warning: { color: "var(--fc-color-warning)", simbolo: "!", live: "polite" },
  info: { color: "var(--fc-color-action, var(--fc-color-text-muted))", simbolo: "i", live: "polite" },
};

const DURACION_MS = 5000;
let siguienteId = 0;

const ESTILOS = `
@keyframes fc-toast-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.fc-toast-local { animation: fc-toast-in 160ms ease-out; }
@media (prefers-reduced-motion: reduce) { .fc-toast-local { animation: none; } }
`;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const cerrar = useCallback((id: number) => {
    setMensajes((xs) => xs.filter((m) => m.id !== id));
    const t = timers.current.get(id);
    if (t) {
      clearTimeout(t);
      timers.current.delete(id);
    }
  }, []);

  const empujar = useCallback(
    (title: string, tono: ToastTono, description?: string) => {
      const id = siguienteId++;
      setMensajes((xs) => [...xs, { id, tono, title, ...(description ? { description } : {}) }]);
      const t = setTimeout(() => cerrar(id), DURACION_MS);
      timers.current.set(id, t);
    },
    [cerrar],
  );

  // Al desmontar, no dejar temporizadores colgando.
  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const t of map.values()) clearTimeout(t);
      map.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      toast: (title, opts) => empujar(title, opts?.tono ?? "info", opts?.description),
      success: (title, description) => empujar(title, "success", description),
      error: (title, description) => empujar(title, "error", description),
      warning: (title, description) => empujar(title, "warning", description),
      info: (title, description) => empujar(title, "info", description),
    }),
    [empujar],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <style>{ESTILOS}</style>
      {/* Viewport: no captura clics (así no tapa la UI cuando está vacío); cada
          tarjeta sí. Abajo a la derecha, como el del sistema. */}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
        {mensajes.map((m) => {
          const t = TONO[m.tono];
          return (
            <div
              key={m.id}
              role="status"
              aria-live={t.live}
              className="fc-toast-local pointer-events-auto flex min-w-[16rem] max-w-[22rem] items-start gap-2.5 rounded-panel border border-border bg-surface p-3.5 text-text shadow-sm"
              style={{ borderLeft: `4px solid ${t.color}` }}
            >
              <span
                aria-hidden="true"
                className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-2xs font-bold text-on-action"
                style={{ background: t.color }}
              >
                {t.simbolo}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{m.title}</p>
                {m.description ? (
                  <p className="mt-0.5 break-words text-sm text-text-muted">{m.description}</p>
                ) : null}
              </div>
              <button
                type="button"
                aria-label="Cerrar"
                onClick={() => cerrar(m.id)}
                className="-mr-1 -mt-1 rounded-control px-1.5 text-text-muted hover:text-text"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
